import { isEqual, set, forEach, map, get, filter, cloneDeep } from 'lodash';
import turf from '@turf/turf';
import csv from 'csvtojson';

import { checkValueIsNumber, checkArrayForNumbers } from '../check-for-number';
import { readUploadedFileAsText } from '../export-import-files';
import { pointsCollectionFeatureToLineString } from '../geojson-utilities';

const DEFAULT_GPSCP_ARRAY = [0, 0, 0];
const MAX_NUMBER_OF_POINTS = 30000;
const GO_PRO_CSV_HEADERS_ARRAY = [
  'Milliseconds',
  'GPSTime',
  'Latitude',
  'Longitude',
  'Altitude',
  'Speed',
  'Speed3D',
  'TS',
  'GpsAccuracy',
  'GpsFix'
];

/**
 * Takes a csv file and creates the track for the video from the columns, which must be defined as follows. Also creates
the control points and the array of points of the flight, and checks if the corners of the image are defined.

['Milliseconds','Latitude','Longitude','Altitude','Speed','Speed3D','TS','GpsAccuracy','GpsFix'].
 *
 * @async
 * @param {File} file - The csv file to read.
 * @param {number} dateGPSRecording - N - The GPS date of the video recording.
 * @returns {object} An object containing the array of feature Points, a feature LineString representing the video track
 * and a Feature Collection of Points with the control points.
 * @throws {Error} Error if anything goes wrong or file is not kml.
 */
const createTrackFromGoProCSV = async (file, dateGPSRecording) => {
  let lastFilterMilSec = 0;
  let milSecInt;
  let accumulatedDistance = 0;
  let lastDistance = 0;
  const csvControlPoints = [];

  const checkFormatNumMilSec = milsec => {
    const isFloat = milsec.includes('.');
    if (!isFloat) milSecInt = parseInt(milsec, 10);

    return milSecInt;
  };

  try {
    const fileExtension = file.name.substring(file.name.lastIndexOf('.') + 1, file.name.length);
    const headersRow = {};
    forEach(GO_PRO_CSV_HEADERS_ARRAY, header => set(headersRow, header, header));

    const csvText = await readUploadedFileAsText(file);
    const csvDataArray = await csv({
      noheader: true,
      headers: GO_PRO_CSV_HEADERS_ARRAY
    }).fromString(csvText);

    if (!isEqual(fileExtension, 'csv'))
      if (!isEqual(fileExtension, 'CSV')) throw new Error('No valid file extension file goPro');

    if (!isEqual(headersRow, csvDataArray[0]))
      throw new Error('Invalid CSV file, check for errors in headers');

    if (!isEqual(dateGPSRecording, csvDataArray[1].GPSTime))
      throw new Error(
        'The GPS date of the track and the date of the video recording are different'
      );

    if (csvDataArray.length >= MAX_NUMBER_OF_POINTS) {
      const filterCSVArray = cloneDeep(csvDataArray);
      csvDataArray.splice(0, csvDataArray.length);
      map(filterCSVArray, (object, index) => {
        if (index <= 1) csvDataArray.push(object);

        if (index > 1) {
          const milSecObj = checkFormatNumMilSec(object.Milliseconds);
          if (milSecObj - lastFilterMilSec >= 2000) {
            csvDataArray.push(object);
            lastFilterMilSec = milSecObj;
          }
        }
        if (index === filterCSVArray.length - 1) {
          csvDataArray.push(object);
        }
      });

      if (csvDataArray.length >= MAX_NUMBER_OF_POINTS)
        throw new Error('This file is too large. Please, pick another one.');
    }

    map(csvDataArray, (object, index, array) => {
      if (index <= 1) {
        csvControlPoints.push({ point: object, distTravelled: 0 });
      }
      if (index > 1) {
        const point = object;
        const prevPoint = array[index - 1];
        const distance = turf.distance(
          [parseFloat(prevPoint.Longitude), parseFloat(prevPoint.Latitude)],
          [parseFloat(point.Longitude), parseFloat(point.Latitude)]
        );
        accumulatedDistance += distance;

        if (accumulatedDistance >= 0.2 && lastDistance === 0) {
          csvControlPoints.push({
            point: object,
            distTravelled: accumulatedDistance
          });
          lastDistance = accumulatedDistance;
        } else if (accumulatedDistance - lastDistance >= 0.35 || index === array.length - 1) {
          csvControlPoints.push({
            point: object,
            distTravelled: accumulatedDistance
          });
          lastDistance = accumulatedDistance;
        }
      }
    });

    const newFeatureArray = map(
      filter(csvDataArray, (csvRow, index) => index > 0),
      (csvRow, index, array) => {
        const GPSCP = checkArrayForNumbers({
          valuesArray: [csvRow.Longitude, csvRow.Latitude, csvRow.Altitude],
          defaultValuesArray: DEFAULT_GPSCP_ARRAY
        });

        const properties = {
          distanceTravelled: 0,
          isControlPoint: index === 0 || index === array.length - 1,
          timeUS: checkValueIsNumber({ value: csvRow.Milliseconds * 1000, defaultValue: 0 })
        };

        return turf.point(GPSCP, properties);
      }
    );

    const newFeatureArrayControlPoints = map(
      filter(csvControlPoints, (csvRow, index) => index > 0),
      csvRow => {
        const GPSCP = checkArrayForNumbers({
          valuesArray: [csvRow.point.Longitude, csvRow.point.Latitude, csvRow.point.Altitude],
          defaultValuesArray: DEFAULT_GPSCP_ARRAY
        });

        const properties = {
          distanceTravelled: csvRow.distTravelled,
          isControlPoint: true,
          timeUS: checkValueIsNumber({ value: csvRow.point.Milliseconds * 1000, defaultValue: 0 })
        };

        return turf.point(GPSCP, properties);
      }
    );

    const newTrack = pointsCollectionFeatureToLineString(turf.featureCollection(newFeatureArray));

    const newControlPoints = turf.featureCollection(
      filter(newFeatureArrayControlPoints, feature => get(feature, 'properties.isControlPoint'))
    );

    return {
      newFeatureArray,
      newTrack,
      newControlPoints
    };
  } catch (error) {
    throw new Error(error.message);
  }
};
export default createTrackFromGoProCSV;
